import { Unit } from 'api/units/units.api'
import classNames from 'classnames'
import { getDisplayName } from 'helpers/global.helper/global.helper'
import { DateTimePickerRange } from 'helpers/settingsModal.helper/settingsModal.helper'
import { useSettingsModalTitle } from 'hooks/utilsHooks/utilsHooks'
import moment, { Moment } from 'moment'
import React, { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SettingsModal from 'ui/components/SettingsModal/SettingsModal'
import { SettingsPermissions } from 'ui/components/SettingsTable/SettingsTable'
import UnitSettingForm, { UnitSettingFormData } from 'ui/components/UnitSettingForm/UnitSettingForm'
import Datetime from 'utils/datetime/datetime'
import {
  getCellHeight,
  getNumberOfSettingsPerRow,
  getSumOfPreviousRowsSettings,
  RowUnitIndexAndSettings,
  SettingsCalendarItem,
  SettingsCalendarItemSetting,
} from '../SettingsCalendarHelper'

import Setting from './Setting/Setting'
import styles from './SettingsCalendar.module.less'
import SettingsCalendarCell from './SettingsCalendarCell/SettingsCalendarCell'

type SettingsCalendarProps = {
  startTime: ISODateTime
  endTime: ISODateTime
  permissions: SettingsPermissions
  startEndTimeRange?: DateTimePickerRange
  items: SettingsCalendarItem[]
  CELL_WIDTH: number
}

type DefaultValues = {
  start_time?: Moment
  end_time?: Moment
  id?: number
  objectProperty?: number
  comment: string | null
  value?: number | string
  isEditable?: boolean
}

export const CELL_HEIGHT = 30

export default function SettingsCalendar({
  startTime,
  endTime,
  permissions,
  startEndTimeRange,
  items,
  CELL_WIDTH,
}: SettingsCalendarProps): ReactElement {
  const { t } = useTranslation()

  const dates: ISODateTime[] = Datetime.getDatesBetween(startTime, endTime)
  const currentDate: ISODateTime = Datetime.getISONow(0)

  const [openCreateNewSettingModal, setOpenCreateNewSettingModal] = useState(false)
  const [newSettingItem, setNewSettingItem] = useState<Unit | undefined>(undefined)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [defaultValues, setDefaultValues] = useState<DefaultValues>()

  const closeModal = (): void => {
    setOpenCreateNewSettingModal(false), setIsEditing(false), setNewSettingItem(undefined)
  }

  const editSetting = (setting: SettingsCalendarItemSetting): void => {
    const unit = items.find((unit) => unit.id === setting.parentId)
    if (unit) {
      setNewSettingItem(unit)
      setIsEditing(true)
      setDefaultValues({
        start_time: moment(setting.start_time),
        end_time: setting.end_time && moment(setting.end_time),
        id: setting.id,
        objectProperty: setting.object_property,
        comment: setting.comment,
        value: setting.value.toString(),
        isEditable: setting.isEditable,
      })

      setOpenCreateNewSettingModal(true)
    }
  }

  const rowCellHeights: RowUnitIndexAndSettings[] = useMemo(() => {
    return getNumberOfSettingsPerRow(items)
  }, [items])

  const modalTitle = useSettingsModalTitle({
    unit: newSettingItem,
    editing: defaultValues ? defaultValues.isEditable : false,
  })

  return (
    <>
      <SettingsModal
        closeModal={closeModal}
        open={openCreateNewSettingModal}
        title={modalTitle}
        form={
          openCreateNewSettingModal ? (
            <UnitSettingForm
              permissions={permissions}
              startEndTimeRange={startEndTimeRange}
              isEditing={isEditing}
              period={{ startTime: startTime, endTime: endTime }}
              unit={newSettingItem as Unit}
              onFormFinished={closeModal}
              defaultValues={defaultValues as Partial<UnitSettingFormData> | undefined}
            />
          ) : null
        }
      />
      <div className={styles.SettingsCalendar}>
        <div className={styles.SettingsCalendar_FreezeColumn}>
          <div className={styles.SettingsCalendar_Column_Cell}>{t('Unit')}</div>

          {items.map((item, index) => {
            const height = getCellHeight(rowCellHeights, index)
            return (
              <div key={index} style={{ height }} className={styles.SettingsCalendar_Column_Cell}>
                {getDisplayName(item as DisplayNameObject)}
              </div>
            )
          })}
        </div>

        <div className={styles.SettingsCalendar_ColumnWrapper}>
          {dates.map((date, dateIndex) => (
            <div
              key={dateIndex}
              className={classNames({
                [styles.SettingsCalendar_Column__weekend]: Datetime.isWeekday(date),
              })}
            >
              <div className={styles.SettingsCalendar_Column}>
                <div
                  className={classNames({
                    [styles.SettingsCalendar_Column_Header]: true,
                    [styles.SettingsCalendar_Column_Header__today]: Datetime.isSameDate(currentDate, date),
                  })}
                >
                  {Datetime.toLocalTime(date, 'shortDayTextWithMonth')}
                </div>
                {items.map((_item, itemIndex) => {
                  const cellHeight = getCellHeight(rowCellHeights, itemIndex)
                  return (
                    <SettingsCalendarCell
                      key={itemIndex}
                      rowIndex={itemIndex}
                      CELL_HEIGHT={cellHeight}
                      CELL_WIDTH={CELL_WIDTH}
                      onClick={() => {
                        if (
                          permissions.canEdit &&
                          Datetime.isAfter(Datetime.getEndOfDay(date), Datetime.getISONow(0))
                        ) {
                          setOpenCreateNewSettingModal(true)
                        }
                      }}
                      onMouseDown={() => {
                        if (
                          permissions.canEdit &&
                          Datetime.isAfter(Datetime.getEndOfDay(date), Datetime.getISONow(0))
                        ) {
                          setNewSettingItem(_item)
                          setDefaultValues({
                            start_time: moment(date),
                            end_time: moment(Datetime.getEndOfDay(date)),
                            comment: null,
                          })
                        }
                      }}
                    />
                  )
                })}
              </div>
            </div>
          ))}

          {/* Place out settings */}
          {items.map((unit, i) => {
            const result = getSumOfPreviousRowsSettings(rowCellHeights, i)
            return unit.settings.map((setting, j) => {
              return (
                <Setting
                  permissions={permissions}
                  onClick={() => {
                    editSetting(setting)
                  }}
                  CELL_HEIGHT={CELL_HEIGHT}
                  CELL_WIDTH={CELL_WIDTH}
                  previousSettings={result}
                  numberOfRows={items.length}
                  unitIndex={i}
                  settingIndex={j}
                  setting={setting}
                  calendarStartTime={startTime}
                  calendarEndTime={endTime}
                  key={`${i}_${j}`}
                />
              )
            })
          })}
        </div>
      </div>
    </>
  )
}
